import { useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Button,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchPlayers, updateBonusPoints } from "@/store/playersSlice";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import { LoadingBox } from "@components/LoadingBox";
import { PAGE_CONTAINER_SX } from "@/constants/layout";

export default function BonusPoints() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: players, loading } = useAppSelector((s) => s.players);
  const { notify } = useNotification();

  useEffect(() => {
    dispatch(fetchPlayers());
  }, [dispatch]);

  const handleChange = async (id: string, current: number, delta: number) => {
    try {
      await dispatch(
        updateBonusPoints({ id, bonusPoints: current + delta }),
      ).unwrap();
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  if (loading) {
    return <LoadingBox minHeight={240} />;
  }

  const hasPlayers = players.length > 0;

  return (
    <Box sx={{ ...PAGE_CONTAINER_SX, maxWidth: 640, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("bonus.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 0.5,
            }}
          >
            {t("bonus.subtitle")}
          </Typography>
        </Box>
        {hasPlayers && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate("/players")}
            sx={{ alignSelf: { xs: "stretch", sm: "center" }, flexShrink: 0 }}
          >
            {t("bonus.managePlayers")}
          </Button>
        )}
      </Stack>
      {!hasPlayers ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: "8px",
            borderStyle: "dashed",
            borderColor: "divider",
            bgcolor: "action.hover",
            py: 5,
            px: 3,
            textAlign: "center",
          }}
        >
          <PeopleOutlinedIcon
            sx={{ fontSize: 52, color: "text.secondary", opacity: 0.85, mb: 2 }}
          />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("bonus.emptyTitle")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 3,
              maxWidth: 360,
              mx: "auto",
            }}
          >
            {t("bonus.emptyDescription")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/players")}
          >
            {t("bonus.goToPlayers")}
          </Button>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          {players.map((player, i) => (
            <Box key={player.id}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  px: { xs: 2, sm: 2.5 },
                  py: 2,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {player.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    DEF {player.defense} · AGI {player.agility} · TEC{" "}
                    {player.technique}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    justifyContent: { xs: "space-between", sm: "flex-end" },
                    flexShrink: 0,
                    pl: { xs: 0, sm: 2 },
                    minWidth: { sm: 200 },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: { xs: "none", sm: "block" },
                      mr: 0.5,
                    }}
                  >
                    {t("bonus.bonusLabel")}
                  </Typography>
                  <Tooltip title={t("bonus.decrease")}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleChange(player.id, player.bonusPoints, -1)
                        }
                        disabled={player.bonusPoints <= 0}
                        aria-label={t("bonus.decrease")}
                      >
                        <RemoveCircleOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Chip
                    label={player.bonusPoints}
                    color="primary"
                    variant="outlined"
                    sx={{ minWidth: 52, fontWeight: 700 }}
                  />
                  <Tooltip title={t("bonus.increase")}>
                    <span>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() =>
                          handleChange(player.id, player.bonusPoints, 1)
                        }
                        aria-label={t("bonus.increase")}
                      >
                        <AddCircleOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {i < players.length - 1 && <Divider />}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
